import React, { useMemo } from "react";
import { Tab } from "../../../../components/layout/tabbed-interface/components/types";
import type { Order } from "../../../orders/utils/types";
import { FinancialReportTab } from "./FinancialReportTab";
import { PROFIT_MODE, RegisterSaveFn } from "./types";

const inputSx = {
  height: 28,
  fontSize: 13,
  px: 0.75,
};

export const useOrderProfitTabs = (
  Order: Order,
  editMode: boolean,
  onRegisterSave?: (_fn: RegisterSaveFn) => void,
  onDirtyChange?: (_dirty: boolean) => void,
) => {
  const estimatedReport = Order.estimatedFinancialReport;
  const actualReport = Order.actualFinancialReport;
  const overriddenReport = Order.overriddenFinancialReport;

  const shouldShowOverriddenTab = useMemo(() => {
    if (!overriddenReport) return false;

    return Object.values(overriddenReport).some(
      (value) => typeof value === "number" && value !== 0,
    );
  }, [overriddenReport]);

  // ✅ NEW
  const shouldShowActualTab = useMemo(() => {
    return !!actualReport;
  }, [actualReport]);

  const tabs = useMemo<Tab[]>(() => {
    const result: Tab[] = [];

    if (shouldShowOverriddenTab && overriddenReport) {
      result.push({
        label: "overridden",
        path: PROFIT_MODE.OVERRIDDEN,
        render: () => (
          <FinancialReportTab
            key={PROFIT_MODE.OVERRIDDEN}
            financialReport={overriddenReport}
            actualReport={actualReport}
            mode={PROFIT_MODE.OVERRIDDEN}
            editMode={editMode}
            inputSx={inputSx}
            onRegisterSave={onRegisterSave}
            onDirtyChange={onDirtyChange}
          />
        ),
      });
    }

    if (shouldShowActualTab && actualReport) {
      result.push({
        label: "actual",
        path: PROFIT_MODE.ACTUAL,
        render: () => (
          <FinancialReportTab
            key={PROFIT_MODE.ACTUAL}
            financialReport={actualReport}
            mode={PROFIT_MODE.ACTUAL}
            editMode={editMode}
            inputSx={inputSx}
            onRegisterSave={onRegisterSave}
            onDirtyChange={onDirtyChange}
          />
        ),
      });
    }

    if (estimatedReport) {
      result.push({
        label: "estimated",
        path: PROFIT_MODE.ESTIMATED,
        render: () => (
          <FinancialReportTab
            key={PROFIT_MODE.ESTIMATED}
            financialReport={estimatedReport}
            mode={PROFIT_MODE.ESTIMATED}
          />
        ),
      });
    }

    return result;
  }, [
    shouldShowOverriddenTab,
    shouldShowActualTab,
    overriddenReport,
    actualReport,
    estimatedReport,
    editMode,
    onRegisterSave,
    onDirtyChange,
  ]);

  return { tabs, shouldShowOverriddenTab, shouldShowActualTab };
};
